import { Cron } from 'croner';
import fetch from 'node-fetch';
import { pino } from 'pino';
import { 
  ServiceStatus, 
  ServiceEvent
} from './types.js';
import type { 
  DeployedService, 
  HealthCheckConfig 
} from './types.js';
import { RegistryDatabase } from './database/index.js';

export interface HealthCheckResult {
  serviceId: string;
  success: boolean;
  status: ServiceStatus;
  responseTime?: number;
  error?: string;
  timestamp: string;
}

export class HealthChecker {
  private logger = pino({ name: 'health-checker' });
  private jobs: Map<string, Cron> = new Map();
  private db: RegistryDatabase;

  constructor(db: RegistryDatabase) {
    this.db = db;
  }

  // 启动服务的健康检查
  async startHealthCheck(service: DeployedService): Promise<void> {
    // 如果已经有任务在运行，先停止
    this.stopHealthCheck(service.id);

    const config = service.healthCheck;
    if (!config?.enabled || !config.endpoint) {
      this.logger.info(`Health check disabled for service ${service.name}`);
      return;
    }

    // 创建定时任务
    const job = new Cron(
      `*/${Math.floor(config.interval / 1000)} * * * * *`, // 转换为秒
      async () => {
        await this.performHealthCheck(service);
      },
      {
        name: `health-check-${service.id}`,
        catch: (error) => {
          this.logger.error({ error, serviceId: service.id }, 'Health check job error');
        }
      }
    );

    this.jobs.set(service.id, job);
    this.logger.info(
      { serviceId: service.id, interval: config.interval },
      `Started health check for service ${service.name}`
    );

    // 立即执行一次健康检查
    await this.performHealthCheck(service);
  }

  // 停止健康检查
  stopHealthCheck(serviceId: string): void {
    const job = this.jobs.get(serviceId);
    if (job) {
      job.stop();
      this.jobs.delete(serviceId);
      this.logger.info({ serviceId }, 'Stopped health check');
    }
  }

  // 执行单次健康检查
  async performHealthCheck(service: DeployedService): Promise<HealthCheckResult> {
    const config = service.healthCheck || this.getDefaultConfig();
    const startTime = Date.now();
    const result: HealthCheckResult = {
      serviceId: service.id,
      success: false,
      status: ServiceStatus.Unknown,
      timestamp: new Date().toISOString()
    };

    try {
      // 构建健康检查URL
      const healthUrl = config.endpoint || `${service.url}/health`;
      
      this.logger.debug({ serviceId: service.id, url: healthUrl }, 'Performing health check');

      // 执行健康检查请求
      const response = await this.fetchWithRetry(healthUrl, {
        timeout: config.timeout,
        retries: config.retries
      });

      const responseTime = Date.now() - startTime;
      result.responseTime = responseTime;

      if (response.ok) {
        result.success = true;
        result.status = ServiceStatus.Running;
        
        // 更新数据库
        this.db.updateServiceStatus(service.id, ServiceStatus.Running);
        this.db.updateLastHealthCheck(service.id, result.timestamp);
        
        // 更新指标
        const currentMetrics = service.metrics || {
          uptime: 0,
          requestCount: 0,
          errorCount: 0,
          errorRate: 0,
          avgResponseTime: 0,
          healthChecksPassed: 0,
          healthChecksFailed: 0
        };

        currentMetrics.healthChecksPassed++;
        currentMetrics.lastResponseTime = responseTime;
        
        // 计算平均响应时间
        const totalChecks = currentMetrics.healthChecksPassed + currentMetrics.healthChecksFailed;
        currentMetrics.avgResponseTime = 
          (currentMetrics.avgResponseTime * (totalChecks - 1) + responseTime) / totalChecks;

        this.db.updateServiceMetrics(service.id, currentMetrics);

        // 记录事件
        this.db.recordEvent({
          id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          serviceId: service.id,
          event: ServiceEvent.HealthCheckPassed,
          timestamp: result.timestamp,
          details: { responseTime }
        });

        this.logger.debug(
          { serviceId: service.id, responseTime },
          'Health check passed'
        );
      } else {
        throw new Error(`Health check failed with status ${response.status}`);
      }
    } catch (error: any) {
      result.success = false;
      result.status = ServiceStatus.Unhealthy;
      result.error = error.message;

      // 更新数据库
      this.db.updateServiceStatus(service.id, ServiceStatus.Unhealthy);
      this.db.updateLastHealthCheck(service.id, result.timestamp);

      // 更新指标
      const currentMetrics = service.metrics || {
        uptime: 0,
        requestCount: 0,
        errorCount: 0,
        errorRate: 0,
        avgResponseTime: 0,
        healthChecksPassed: 0,
        healthChecksFailed: 0
      };

      currentMetrics.healthChecksFailed++;
      currentMetrics.errorCount++;
      
      // 计算错误率
      const totalChecks = currentMetrics.healthChecksPassed + currentMetrics.healthChecksFailed;
      currentMetrics.errorRate = currentMetrics.healthChecksFailed / totalChecks;

      this.db.updateServiceMetrics(service.id, currentMetrics);

      // 记录事件
      this.db.recordEvent({
        id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        serviceId: service.id,
        event: ServiceEvent.HealthCheckFailed,
        timestamp: result.timestamp,
        details: { error: error.message }
      });

      // 记录操作结果
      this.db.recordOperation({
        serviceId: service.id,
        operation: 'health-check' as any,
        success: false,
        error: error.message,
        timestamp: result.timestamp,
        duration: Date.now() - startTime
      });

      this.logger.error(
        { serviceId: service.id, error: error.message },
        'Health check failed'
      );
    }

    return result;
  }

  // 批量健康检查
  async checkAllServices(): Promise<HealthCheckResult[]> {
    const services = this.db.listServices({ status: ServiceStatus.Running });
    const results: HealthCheckResult[] = [];

    for (const service of services) {
      if (service.healthCheck?.enabled) {
        const result = await this.performHealthCheck(service);
        results.push(result);
      }
    }

    return results;
  }

  // 启动所有服务的健康检查
  async startAll(): Promise<void> {
    const services = this.db.listServices();
    for (const service of services) {
      if (service.status === ServiceStatus.Running && service.healthCheck?.enabled) {
        await this.startHealthCheck(service);
      }
    }
  }

  // 停止所有健康检查
  stopAll(): void {
    for (const [serviceId, job] of this.jobs) {
      job.stop();
    }
    this.jobs.clear();
    this.logger.info('Stopped all health checks');
  }

  // 工具方法
  private async fetchWithRetry(
    url: string, 
    options: { timeout: number; retries: number }
  ): Promise<any> {
    let lastError: Error | null = null;

    for (let i = 0; i <= options.retries; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), options.timeout);

        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            'User-Agent': 'ServiceRegistry/1.0'
          }
        });

        clearTimeout(timeoutId);
        return response;
      } catch (error: any) {
        lastError = error;
        if (i < options.retries) {
          // 指数退避
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
        }
      }
    }

    throw lastError || new Error('Health check failed');
  }

  private getDefaultConfig(): HealthCheckConfig {
    return {
      enabled: true,
      interval: 30000,
      timeout: 5000,
      retries: 3
    };
  }

  // 获取健康检查状态
  getStatus(): { total: number; active: number; services: string[] } {
    const activeServices = Array.from(this.jobs.keys());
    return {
      total: this.jobs.size,
      active: activeServices.length,
      services: activeServices
    };
  }
}